import React, { useState, useEffect } from 'react';
import { startFileMigration, getFileMigrationStatus, resetFileMigration } from '../services/FileMigration.service';

interface FileMigrationStatus {
  isRunning: boolean;
  totalFiles: number;
  processedFiles: number;
  successCount: number;
  errorCount: number;
  currentBookId?: string;
  errors: string[];
  startTime?: string;
  endTime?: string;
}

const FileMigration: React.FC = () => {
  const [migrationStatus, setMigrationStatus] = useState<FileMigrationStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchMigrationStatus();
    // Poll for status updates every 5 seconds if migration is running
    const interval = setInterval(() => {
      if (migrationStatus?.isRunning) {
        fetchMigrationStatus();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [migrationStatus?.isRunning]);

  const fetchMigrationStatus = async () => {
    try {
      const response = await getFileMigrationStatus();
      if (response.status === 200) {
        setMigrationStatus(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch migration status:', err);
    }
  };

  const handleStartMigration = async () => {
    if (!migrationStatus?.isRunning) {
      setIsLoading(true);
      setError(null);
      setSuccess(null);

      try {
        const response = await startFileMigration();
        if (response.status === 200) {
          setSuccess('File migration started successfully');
          fetchMigrationStatus();
        } else {
          setError(response.message || 'Failed to start migration');
        }
      } catch (err) {
        setError('Failed to start migration');
        console.error('Migration error:', err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleResetMigration = async () => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await resetFileMigration();
      if (response.status === 200) {
        setSuccess('Migration status reset successfully');
        fetchMigrationStatus();
      } else {
        setError(response.message || 'Failed to reset migration');
      }
    } catch (err) {
      setError('Failed to reset migration');
      console.error('Reset error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getProgressPercentage = () => {
    if (!migrationStatus || migrationStatus.totalFiles === 0) return 0;
    return Math.round((migrationStatus.processedFiles / migrationStatus.totalFiles) * 100);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">File Migration</h2>
        <div className="flex gap-2">
          <button
            onClick={handleStartMigration}
            disabled={isLoading || migrationStatus?.isRunning}
            className={`px-4 py-2 rounded-md font-medium ${
              migrationStatus?.isRunning
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {isLoading ? 'Starting...' : 'Start Migration'}
          </button>
          <button
            onClick={handleResetMigration}
            disabled={isLoading}
            className="px-4 py-2 bg-gray-600 text-white rounded-md font-medium hover:bg-gray-700"
          >
            Reset Status
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
          {success}
        </div>
      )}

      {migrationStatus && (
        <div className="space-y-4">
          {/* Status Overview */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-sm font-medium text-gray-500">Status</div>
              <div className={`text-lg font-semibold ${
                migrationStatus.isRunning ? 'text-blue-600' : 'text-gray-900'
              }`}>
                {migrationStatus.isRunning ? 'Running' : 'Idle'}
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-sm font-medium text-gray-500">Total Files</div>
              <div className="text-lg font-semibold text-gray-900">
                {migrationStatus.totalFiles}
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-sm font-medium text-gray-500">Processed</div>
              <div className="text-lg font-semibold text-gray-900">
                {migrationStatus.processedFiles}
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-sm font-medium text-gray-500">Success</div>
              <div className="text-lg font-semibold text-green-600">
                {migrationStatus.successCount}
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          {migrationStatus.totalFiles > 0 && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Progress</span>
                <span className="text-sm text-gray-500">
                  {getProgressPercentage()}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${getProgressPercentage()}%` }}
                ></div>
              </div>
            </div>
          )}

          {/* Current Activity */}
          {migrationStatus.isRunning && migrationStatus.currentBookId && (
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-sm font-medium text-blue-700">Currently Processing</div>
              <div className="text-lg font-semibold text-blue-900">
                Book ID: {migrationStatus.currentBookId}
              </div>
            </div>
          )}

          {/* Error Summary */}
          {migrationStatus.errorCount > 0 && (
            <div className="bg-red-50 p-4 rounded-lg">
              <div className="text-sm font-medium text-red-700 mb-2">
                Errors ({migrationStatus.errorCount})
              </div>
              <div className="max-h-32 overflow-y-auto">
                {migrationStatus.errors.slice(-5).map((error, index) => (
                  <div key={index} className="text-sm text-red-600 mb-1">
                    • {error}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Timing Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-sm font-medium text-gray-500">Start Time</div>
              <div className="text-sm text-gray-900">
                {formatDate(migrationStatus.startTime)}
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-sm font-medium text-gray-500">End Time</div>
              <div className="text-sm text-gray-900">
                {formatDate(migrationStatus.endTime)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileMigration;
